<script setup>
import {computed, onActivated, onDeactivated, reactive, ref, watch} from "vue";
defineOptions({ name: 'CallsCompletedView' })
import {useCallStore} from "../../store/calls.js";
import {useAuthStore} from "../../store/auth.js";
import EmptyState from "../../components/global/EmptyState.vue";
import SectionHeader from "../../components/section/SectionHeader.vue";
import Pagination from "../../components/section/Pagination.vue";
import CallRowItem from "../../components/calls/CallRowItem.vue";
import {useModalStore} from "../../store/modal.js";
import ErrorBar from "../../components/global/ErrorBar.vue";

const modalStore = useModalStore()
const store = useCallStore()
const authStore = useAuthStore()
const scrollSection = ref()
const isAdmin = computed(() => ['reseller-super-admin', 'reseller-team-leader', 'super-admin', 'internal-super-admin', 'internal-team-leader'].includes(authStore.user.roles[0].name))
let searchDebounce
const search = ref()

const filter_data = ref()
const callParams = reactive({
    sort_direction: 'desc',
    assignee_id: isAdmin.value ? null : authStore.user.id,
    call_type: null,
    limit: 25,
    start_date: null,
    end_date: null,
    complete: 1
})

const handleSearch = async (query) => {
    await store.searchCalls(query)
}

const clearDateFilters = () => {
    callParams.start_date = null
    callParams.end_date = null
}

const clearOtherFilters = () => {
    callParams.sort_direction = 'desc'
    callParams.assignee_id = isAdmin.value ? null : authStore.user.id
    callParams.call_type = null
    callParams.start_date = null
    callParams.end_date = null
    callParams.complete = 1
}

watch((search), async (value) => {
    clearTimeout(searchDebounce)
    if (value) {
        searchDebounce = setTimeout(async () => {
            await handleSearch(value)
        }, 250)
    } else {
        await getCalls()
    }
})

const dateError = computed(() => {
    const { start_date, end_date } = callParams

    if (start_date && end_date && start_date > end_date) {
        return 'Start date cannot be after the end date.'
    }

    return ''
})

const getCalls = async (page = 1) => {
    if (dateError.value) {
        return
    }

    await store.getCalls({ ...callParams, page })

    if (store.calls?.data.length) {
        scrollSection.value.scrollTop = 0
    }

}

// onMounted(async () => {
//     await getCalls()
//     await store.prepareFilterData()
//     filter_data.value = store.filter_data
// })

onActivated(async () => {
    await getCalls()
    await store.prepareFilterData()
    filter_data.value = store.filter_data
})

onDeactivated(() => {
    store.resetCalls()
})

</script>

<template>
    <main v-if="!store.calls">
        <empty-state icon="call-voice" heading="Loading calls." content="Give it sec, it's coming in a mo." />
    </main>

    <main v-else-if="store.calls.data.length">
        <section>
            <section-header :heading="`${isAdmin ? 'All completed calls.' : 'Your completed calls.'}`">
                <div class="search-bar">
                    <brand-form-input label="Search by call type or order nr" variant="search" v-model="search" />
                    <button class="mobile-filter-button" @click.prevent="modalStore.open('CompleteCall', {id: call.id}, 'body', 'bottom-right')">
                        <ah-icon name="filter-default" />
                    </button>
                </div>
            </section-header>
            <div class="section-table" ref="scrollSection" v-if="store.calls.data.length">
                <call-row-item :call v-for="call in store.calls.data" />
            </div>
            <div class="section-table-empty" v-if="!store.calls.data.length">
                <h1>No tickets match</h1>
            </div>
            <div class="section-footer" v-if="store.calls.data.length">
                <pagination :pagination="store.calls" @change-page="getCalls" />
            </div>
        </section>
        <aside class="section-sidebar">
            <div class="sidebar-header">
                <h2>Filters</h2>
                <p @click.prevent="clearOtherFilters">Clear</p>
            </div>
            <div v-if="dateError">
                <error-bar :error="dateError" />
            </div>
            <div class="sidebar-select">
                <brand-form-select variant="sidebar" label="Sorting." :options="filter_data?.sorting ?? []" v-model="callParams.sort_direction" />
            </div>

            <div class="sidebar-select" v-if='isAdmin'>
                <brand-form-select variant="sidebar" label="Assignee." :options="filter_data?.assignees ?? []" v-model="callParams.assignee_id" />
            </div>

             <div class="sidebar-select">
                <brand-form-select variant="sidebar" label="Call type." :options="filter_data?.call_types ?? []" v-model="callParams.call_type" />
            </div>

            <div class="sidebar-header">
                <h2>Date</h2>
                <p @click.prevent="clearDateFilters">Clear</p>
            </div>
            <brand-form-input label="Start date." variant="date" v-model="callParams.start_date" />
            <brand-form-input label="End date." variant="date" v-model="callParams.end_date" />

            <div class="sidebar-footer">
                <brand-button as="button" variant="primary" @click.prevent="getCalls(1)">Apply</brand-button>
                <brand-button as="button" variant="small"><ah-icon name="arrow-in" /></brand-button>
            </div>
        </aside>
    </main>

     <main v-else>
        <empty-state icon="cart-default" heading="No pending orders." content="Once an order is placed it will show up here." />
    </main>
</template>

<style scoped lang="scss">
.search-bar {
    min-width: 34rem;
}
</style>
